import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { entities, functions, supabase } from '@/api';
import { useAuth } from '@/lib/AuthContext';
import { keepIfSame } from '@/lib/sameData';
import { fusionneLeads, relireDepuis } from '@/lib/leadsIncrement';

const DataContext = createContext(null);

// archive_leadgen_2025 : import ponctuel des ~575 leads perdus 2024-2025 sortis
// des boards synchronisés. Chargé comme les boards live pour qu'ils comptent
// dans les leads générés (dénominateur coût/lead, entonnoir) — 0 signature.
const BOARDS = ['webforce', 'digicommerce', 'coldcall', 'archive_leadgen_2025'];
const PAGE_SIZE = 1000;

async function fetchBoardLeads(boardKey) {
  const all = [];
  let from = 0;
  for (;;) {
    const to = from + PAGE_SIZE - 1;
    const { data, error } = await supabase
      .from('lead_unified')
      .select('*')
      .eq('board_key', boardKey)
      // CRITICAL: secondary 'id' order is the tie-breaker for stable
      // paginated reads. Without it, rows that share a created_at (e.g.
      // 72 webforce rows imported in the same batch had the same
      // nano-second timestamp) can be returned twice across pages OR
      // skipped entirely — that caused 54 webforce items to dupe in
      // the rawLeads array, which dedupeLeads then collapsed back to
      // 2639 instead of the real 2693.
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .range(from, to);
    if (error) throw error;
    if (!data || data.length === 0) break;
    all.push(...data);
    if (data.length < PAGE_SIZE) break;
    from += PAGE_SIZE;
    if (from > 50_000) break;
  }
  return all;
}

// Leads modifiés depuis `depuis` (updated_at), tous boards : quelques lignes au lieu des 7 000.
async function fetchLeadsModifies(depuis) {
  const all = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    const { data, error } = await supabase
      .from('lead_unified')
      .select('*')
      .in('board_key', BOARDS)
      .gte('updated_at', depuis)
      .order('updated_at')
      .order('id')
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw error;
    all.push(...(data || []));
    if (!data || data.length < PAGE_SIZE) return all;
  }
}

// Relecture complète de temps en temps : un lead supprimé de Monday n'a plus de ligne à relire.
const RELECTURE_COMPLETE_MS = 15 * 60_000;

function dedupeLeads(rawLeads) {
  const seen = new Map();
  for (const lead of rawLeads) {
    // Key on (item_id, board_key) — matches the DB UNIQUE constraint.
    // Keying on item_id alone collapsed leads that legitimately exist in
    // multiple boards (54 cross-board collisions on webforce caused
    // 'Leads en base' to read 2650 instead of the real 2692).
    const key = (lead.item_id || '') + ':' + (lead.board_key || '');
    if (!lead.item_id) continue;
    const existing = seen.get(key);
    if (!existing) { seen.set(key, lead); continue; }
    const leadTs = lead.monday_updated_at || '';
    const existTs = existing.monday_updated_at || '';
    if (leadTs > existTs) { seen.set(key, lead); continue; }
    if (leadTs === existTs) {
      const leadScore =
        (lead.is_delegated_callcenter ? 2 : 0) +
        (lead.is_callcenter_generated_win ? 2 : 0) +
        (lead.is_signed ? 1 : 0);
      const existScore =
        (existing.is_delegated_callcenter ? 2 : 0) +
        (existing.is_callcenter_generated_win ? 2 : 0) +
        (existing.is_signed ? 1 : 0);
      if (leadScore > existScore) seen.set(key, lead);
    }
  }
  return Array.from(seen.values());
}

export function DataProvider({ children }) {
  // Per-user company scope. When app_metadata.permitted_companies is
  // set (e.g. ['WEBFORCE']), every derived list (leads, companies,
  // salespersons, sources) is filtered down to that scope BEFORE the
  // page-level filters apply. Admins always have permitted_companies = null.
  const { user } = useAuth();
  const permittedCompanies = useMemo(() => {
    if (user?.is_admin) return null;
    return Array.isArray(user?.permitted_companies) && user.permitted_companies.length > 0
      ? user.permitted_companies.map((c) => String(c).toUpperCase())
      : null;
  }, [user]);

  const [leads, setLeads] = useState([]);
  const [salesReps, setSalesReps] = useState([]);
  const [salesCalendars, setSalesCalendars] = useState([]);
  const [syncStatuses, setSyncStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastFetch, setLastFetch] = useState(null);
  const [error, setError] = useState(null);

  const [filters, setFilters] = useState({
    dateFrom: null,
    dateTo: null,
    assignedCompany: null,
    salesperson: null,
    source: null,
    callcenterOnly: false,
    boardKey: null,
  });

  const refetchTimerRef = useRef(null);
  const derniereRelectureCompleteRef = useRef(0);
  const leadsRef = useRef([]);

  // Les rafraîchissements de fond (toutes les 60 s, au retour sur l'onglet, sur
  // événement Realtime) ne doivent PAS repasser l'application en « chargement » :
  // les pages remplaçaient alors leurs chiffres par « … » et les graphes
  // rejouaient leur animation à chaque cycle. Seul le tout premier chargement,
  // celui qui n'a encore rien à afficher, allume `loading` ; les suivants
  // passent par `refreshing`, purement indicatif.
  const premierChargementFaitRef = useRef(false);

  const fetchAll = useCallback(async () => {
    const enArrierePlan = premierChargementFaitRef.current;
    if (enArrierePlan) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const [statusArr, reps, cals] = await Promise.all([
        entities.SyncStatus.list('board_key'),
        entities.SalesRep.list(),
        entities.SalesCalendar.list(),
      ]);
      // `keepIfSame` garde le tableau précédent quand le contenu est identique :
      // sans ça, chaque relecture changeait l'identité des données et
      // invalidait tous les calculs dérivés, même sans un octet de différence.
      setSyncStatuses(keepIfSame(Array.isArray(statusArr) ? statusArr : []));
      setSalesReps(keepIfSame(Array.isArray(reps) ? reps : []));
      setSalesCalendars(keepIfSame(Array.isArray(cals) ? cals : []));

      // Tout relire chargeait 7 000 leads (~7 Mo) par onglet et par minute : on ne relit
      // plus que ce qui a changé, et tout seulement toutes les 15 minutes.
      const depuis = relireDepuis(leadsRef.current);
      let suivants;
      if (!depuis || Date.now() - derniereRelectureCompleteRef.current > RELECTURE_COMPLETE_MS) {
        suivants = dedupeLeads((await Promise.all(BOARDS.map(fetchBoardLeads))).flat());
        derniereRelectureCompleteRef.current = Date.now();
      } else {
        suivants = fusionneLeads(leadsRef.current, await fetchLeadsModifies(depuis));
      }
      leadsRef.current = suivants;
      setLeads(keepIfSame(suivants));
      setLastFetch(new Date());
      premierChargementFaitRef.current = true;
    } catch (e) {
      console.error('[dataContext] fetchAll failed', e);
      setError(e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  const scheduleRefetch = useCallback(() => {
    if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
    refetchTimerRef.current = setTimeout(() => {
      fetchAll();
    }, 1500);
  }, [fetchAll]);

  useEffect(() => {
    fetchAll();

    // Safety-net periodic refetch (60s). Realtime is best-effort: a tab
    // that lost its WebSocket or that was background-throttled by Chrome
    // can miss postgres_changes events fired by pg_cron. The interval
    // guarantees the UI converges to the DB state within 60s regardless.
    const intervalId = setInterval(() => { if (document.visibilityState === 'visible') fetchAll(); }, 60_000);

    // Refetch immediately when the tab becomes visible again — covers
    // the "phone in pocket for 2 hours" case where intervals are paused.
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchAll();
    };
    document.addEventListener('visibilitychange', onVisible);

    const channel = supabase
      .channel('dashboard:live')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'lead_unified' },
        scheduleRefetch)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'sync_status' },
        async () => {
          try {
            const status = await entities.SyncStatus.list('board_key');
            setSyncStatuses(keepIfSame(Array.isArray(status) ? status : []));
          } catch (e) {
            console.warn('[dataContext] sync_status refresh failed', e);
          }
        })
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'sales_reps' },
        async () => {
          try {
            const reps = await entities.SalesRep.list();
            setSalesReps(keepIfSame(Array.isArray(reps) ? reps : []));
          } catch (e) {
            console.warn('[dataContext] sales_reps refresh failed', e);
          }
        })
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'sales_calendars' },
        async () => {
          try {
            const cals = await entities.SalesCalendar.list();
            setSalesCalendars(keepIfSame(Array.isArray(cals) ? cals : []));
          } catch (e) {
            console.warn('[dataContext] sales_calendars refresh failed', e);
          }
        })
      .subscribe();

    return () => {
      if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current);
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
      supabase.removeChannel(channel);
    };
  }, [fetchAll, scheduleRefetch]);

  // The /api/sync-monday endpoint now returns 202 immediately and runs the
  // sync in background via Vercel waitUntil. The "running" state is reflected
  // by sync_status.status='running' rows pushed via Realtime, so the UI keeps
  // showing the correct state even after this function resolves.
  const triggerSync = useCallback(async (boardKey = null) => {
    setError(null);
    setSyncing(true);
    try {
      const payload = boardKey ? { board_key: boardKey } : {};
      await functions.invoke('syncMonday', payload);
      // Optimistically refresh sync_status so the UI shows "running" instantly
      // before the Realtime channel catches up.
      const status = await entities.SyncStatus.list('board_key');
      setSyncStatuses(keepIfSame(Array.isArray(status) ? status : []));
    } catch (e) {
      setError(e.message);
    } finally {
      setSyncing(false);
    }
  }, []);

  // Durable "any sync running" indicator, sourced from sync_status table.
  const anyRunning = useMemo(
    () => syncStatuses.some((s) => s.status === 'running'),
    [syncStatuses]
  );

  // Apply per-user company scope BEFORE anything else. Everything
  // downstream (companies dropdown, salespersons list, sources, charts)
  // sees only this slice. Admin / unscoped users see all leads.
  const scopedLeads = useMemo(() => {
    if (!permittedCompanies) return leads;
    return leads.filter((l) =>
      l.assigned_company && permittedCompanies.includes(String(l.assigned_company).toUpperCase())
    );
  }, [leads, permittedCompanies]);

  // Matching commercial désormais EMAIL-FIRST (cf. /admin → CalendarManager).
  // `sales_calendars.calendar_id` est l'email Google du commercial ; et
  // `lead_unified.assigned_to_email` est l'email du Monday user attribué
  // (populé par le sync via Monday users API). Le matching =
  // lead.assigned_to_email === calendar.calendar_id (lower-case).
  const salesCalendarsByEmail = useMemo(() => {
    const m = new Map();
    for (const c of salesCalendars) {
      if (!c.calendar_id) continue;
      m.set(c.calendar_id.toLowerCase(), c);
    }
    return m;
  }, [salesCalendars]);

  // Set des emails de calendars actifs (lower-case) — pour les filtres simples.
  const activeSalesCalendarEmails = useMemo(() => {
    const s = new Set();
    for (const c of salesCalendars) {
      if (c.is_active && c.calendar_id) s.add(c.calendar_id.toLowerCase());
    }
    return s;
  }, [salesCalendars]);

  // Résout un email d'attribution (Monday user OU calendar_id) vers son
  // sales_calendars row. Retourne null si email vide, inconnu, ou calendar
  // inactif. Le caller utilise typiquement `cal.salesperson_name` comme libellé.
  const resolveRepByEmail = useMemo(() => (email) => {
    if (!email || typeof email !== 'string') return null;
    const cal = salesCalendarsByEmail.get(email.toLowerCase());
    if (!cal || cal.is_active === false) return null;
    return cal;
  }, [salesCalendarsByEmail]);

  // Index calendars par tokens du salesperson_name pour fallback nom rapide.
  const salesCalendarTokens = useMemo(() => {
    return salesCalendars
      .filter(c => c.is_active && c.salesperson_name)
      .map(c => ({
        cal: c,
        tokens: c.salesperson_name.toLowerCase().split(/[\s\-,]+/).filter(t => t.length >= 3),
      }));
  }, [salesCalendars]);

  // Resolver unifié : email-first, puis fallback nom (token-match ≥2 chars
  // partagés avec un calendar actif). Utilisé par BySalesperson, Executive,
  // SalesMatchingDiagnostic pour que les 3 vues affichent EXACTEMENT les
  // mêmes chiffres. Accepte le lead entier (pas juste email) parce que
  // certains leads ont assigned_to_primary sans assigned_to_email (Monday
  // users sans email ou comptes désactivés).
  const resolveLeadRep = useMemo(() => (lead) => {
    if (!lead) return null;
    if (!salesCalendars.length) return null;
    // 1. Match par email (le plus fiable)
    const byEmail = salesCalendarsByEmail.get((lead.assigned_to_email || '').toLowerCase());
    if (byEmail && byEmail.is_active) return byEmail;
    // 2. Fallback : token-match sur assigned_to_primary
    const name = (lead.assigned_to_primary || '').trim();
    if (!name || /[@]/.test(name) || name === 'Non assigné' || name === 'Unknown') return null;
    const nameTokens = name.toLowerCase().split(/[\s\-,]+/).filter(t => t.length >= 3);
    if (nameTokens.length < 1) return null;
    for (const c of salesCalendarTokens) {
      const common = c.tokens.filter(t => nameTokens.includes(t)).length;
      if (common >= 2) return c.cal;
      // Cas spécial : nom mono-token long ("Fbuekenhout") qui matche un token
      // de calendar par substring (≥5 chars).
      if (nameTokens.length === 1 && nameTokens[0].length >= 5) {
        if (c.tokens.some(t => t.length >= 5 && (t.includes(nameTokens[0]) || nameTokens[0].includes(t)))) return c.cal;
      }
    }
    return null;
  }, [salesCalendars, salesCalendarsByEmail, salesCalendarTokens]);

  // Déclaré APRÈS resolveLeadRep car le filtre « Commercial » s'appuie dessus :
  // le dropdown liste les noms canoniques des calendriers (salesperson_name),
  // donc le filtre doit résoudre chaque lead vers son rep canonique (même
  // résolution que BySalesperson) — comparer aux noms Monday bruts donnait
  // 0 résultat dès que les noms différaient.
  // Filtres hors période (société, commercial, source, callcenter) — partagés
  // entre la vue par date d'arrivée (filteredLeads, bornée sur lead_date) et le comptage
  // des signatures (signedInPeriodLeads, borné sur date_signed).
  const baseFilteredLeads = useMemo(() => {
    let data = scopedLeads;
    if (filters.boardKey) data = data.filter(l => l.board_key === filters.boardKey);
    if (filters.assignedCompany) data = data.filter(l => l.assigned_company === filters.assignedCompany);
    if (filters.salesperson) {
      if (salesCalendars.length) {
        data = data.filter(l => resolveLeadRep(l)?.salesperson_name === filters.salesperson);
      } else {
        // Pas de calendriers configurés : le dropdown liste les noms bruts.
        data = data.filter(l =>
          l.assigned_to_primary === filters.salesperson ||
          (l.assigned_to_multi || []).includes(filters.salesperson)
        );
      }
    }
    if (filters.source) data = data.filter(l => l.lead_source_normalized === filters.source);
    if (filters.callcenterOnly) data = data.filter(l => l.is_delegated_callcenter);
    return data;
  }, [scopedLeads, filters, salesCalendars.length, resolveLeadRep]);

  const filteredLeads = useMemo(() => {
    let data = baseFilteredLeads;
    // String-compare on YYYY-MM-DD avoids the new Date() pitfall that can
    // produce Invalid Date for weird user input and crash downstream chart
    // components. lead_date and created_at.slice(0,10) are both
    // YYYY-MM-DD so lexicographic compare is exact.
    if (filters.dateFrom && /^\d{4}-\d{2}-\d{2}$/.test(filters.dateFrom)) {
      data = data.filter(l => {
        const d = l.lead_date || l.created_at?.slice(0, 10);
        return d && d >= filters.dateFrom;
      });
    }
    if (filters.dateTo && /^\d{4}-\d{2}-\d{2}$/.test(filters.dateTo)) {
      data = data.filter(l => {
        const d = l.lead_date || l.created_at?.slice(0, 10);
        return d && d <= filters.dateTo;
      });
    }
    return data;
  }, [baseFilteredLeads, filters.dateFrom, filters.dateTo]);

  // Leads SIGNÉS SUR LA PÉRIODE : ceux dont le contrat a été signé entre les
  // deux bornes, d'après `date_signed` — la date lue dans le sheet GRC et
  // recopiée par /api/reconcile-lead-signatures.
  //
  // Aucun repli sur `lead_date` : cette date est posée en masse à l'import
  // (905 leads Digicommerce au 2026-01-15) et faisait apparaître des contrats
  // de 2024 parmi les signatures de l'année en cours. Un lead dont la date de
  // signature est inconnue n'est compté dans aucune période — il apparaît dans
  // `signedDateInconnue`, à arbitrer, plutôt que dans un mauvais mois.
  const signedInPeriodLeads = useMemo(() => {
    const from = /^\d{4}-\d{2}-\d{2}$/.test(filters.dateFrom || '') ? filters.dateFrom : null;
    const to = /^\d{4}-\d{2}-\d{2}$/.test(filters.dateTo || '') ? filters.dateTo : null;
    return baseFilteredLeads.filter(l => {
      if (!l.is_signed) return false;
      // `date_signed` = date du contrat GRC quand il a été retrouvé, sinon date
      // d'arrivée du lead (estimation posée par /api/reconcile-lead-signatures).
      // Aucun lead signé n'est donc perdu, et aucun n'est daté avant son arrivée.
      const d = l.date_signed;
      if (!d) return false;
      if (from && d < from) return false;
      if (to && d > to) return false;
      return true;
    });
  }, [baseFilteredLeads, filters.dateFrom, filters.dateTo]);

  // Leads signés dont aucun contrat GRC n'a pu être retrouvé : leur date de
  // signature est inconnue. `grc_reconciled_at` renseigné avec
  // `grc_match_method` vide = examiné, sans résultat.
  const signedDateInconnue = useMemo(
    () => baseFilteredLeads.filter(l => l.is_signed && l.grc_reconciled_at && !l.grc_match_method),
    [baseFilteredLeads],
  );

  const companies = useMemo(
    () => [...new Set(scopedLeads.map(l => l.assigned_company).filter(Boolean))].sort(),
    [scopedLeads]
  );
  const salespersons = useMemo(() => {
    // Pas de calendars configurés → fallback aux noms bruts (UI non vide).
    if (!salesCalendars.length) {
      return [...new Set((scopedLeads || [])
        .flatMap(l => l.assigned_to_multi || [l.assigned_to_primary])
        .filter(Boolean))].sort();
    }
    // Sinon, list les salesperson_name des calendars ACTIFS qui ont au moins
    // un lead en scope (match par assigned_to_email).
    const usedEmails = new Set();
    for (const l of scopedLeads || []) {
      const emails = l.assigned_to_emails?.length ? l.assigned_to_emails : [l.assigned_to_email];
      for (const e of emails) {
        if (e) usedEmails.add(String(e).toLowerCase());
      }
    }
    const usedNames = new Set();
    for (const email of usedEmails) {
      const cal = salesCalendarsByEmail.get(email);
      if (cal && cal.is_active && cal.salesperson_name) usedNames.add(cal.salesperson_name);
    }
    return [...usedNames].sort();
  }, [scopedLeads, salesCalendars.length, salesCalendarsByEmail]);
  const sources = useMemo(
    () => [...new Set(scopedLeads.map(l => l.lead_source_normalized).filter(Boolean))].sort(),
    [scopedLeads]
  );

  return (
    <DataContext.Provider value={{
      // `leadsIgnoringPeriod` : tous les filtres SAUF la période. Sert aux
      // chiffres datés sur autre chose que `lead_date` (les contrats GRC sont
      // datés à la signature) : ils doivent respecter société / source /
      // commercial sans être bornés par la date d'entrée du lead.
      leads, filteredLeads, leadsIgnoringPeriod: baseFilteredLeads, signedInPeriodLeads, signedDateInconnue, syncStatuses,
      loading, refreshing, syncing, anyRunning, lastFetch, error,
      filters, setFilters,
      companies, salespersons, sources,
      salesReps, salesCalendars, salesCalendarsByEmail, activeSalesCalendarEmails, resolveRepByEmail, resolveLeadRep,
      fetchLeads: fetchAll,
      triggerSync,
    }}>
      {children}
    </DataContext.Provider>
  );
}

export function useData() {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error('useData must be used within DataProvider');
  return ctx;
}
